import type { FeatureCode, Permission } from '@/api/generated/enums';
import { isBuilt } from '@/pages/builtKeys';
import { can, hasFeature } from '@core/session/capability';
import type { Session } from '@core/session/types';

/**
 * The menu, as a manifest.
 *
 * There is deliberately **no menu table in the database** (spec Section 25: role-based menu
 * rendering driven by the permissions the API returns). Structure — label, icon, route, order —
 * is presentation and lives here, so reordering a nav item is not a migration. *Visibility* is
 * derived from `/api/auth/me`, so it can never contradict `[RequiresPermission]` on the endpoint
 * the item routes to.
 *
 * `requires` is typed as {@link Permission}, the `Module:Action` union generated from the C# enums.
 * A typo, or a module renamed on the server, is a compile error rather than a screen that silently
 * never appears for anyone.
 *
 * Labels are literals for now; they become resource keys with i18n in 1.7.9 (rule 13).
 */
export interface MenuNode {
  key: string;
  label: string;
  path?: string;
  /**
   * A Lucide class pair, `icon icon-<name>`, resolved from the font vendored in
   * `style/icons/lucide`. Set on every leaf and on no group: the collapsed rail shows icons and
   * nothing else, so a leaf without one is a row the user cannot identify, while a group heading
   * is not rendered in the rail at all.
   */
  icon?: string;
  /** The grant needed to see it. Absent means everyone signed in. */
  requires?: Permission;
  /** The plan feature it belongs to. Absent means every plan includes it. */
  feature?: FeatureCode;
  children?: MenuNode[];
}

/*
  The order is the day's: the counter's work first, the back office after it, Setup last (owner,
  2026-10-25). The groups below Work are the phone's More screen — Shop, Money, People — so a clerk
  who knows one client finds the same screen under the same word on the other.
*/
export const menu: MenuNode[] = [
  /*
    First, and deliberately carrying no `requires`.

    It is the screen the shell opens on, so gating it on any one module would hand a 403 to
    somebody who holds the other four — which is the defect it was built to end. Every *card*
    inside is gated on its own permission and feature instead, so a person with no rental grants
    sees a thinner screen rather than a refusal. See `pages/today/today.ts`.
  */
  {
    key: 'today',
    icon: 'icon icon-sun',
    label: 'Today',
    path: '/today',
  },
  {
    key: 'rental',
    icon: 'icon icon-calendar-days',
    label: 'Rental',
    children: [
      {
        key: 'bookings',
        icon: 'icon icon-clipboard-list',
        label: 'Bookings',
        path: '/bookings',
        requires: 'RentalOrder:View',
        feature: 'RentalOrders',
      },
      {
        // The photograph-led catalogue over a pair of dates — design cards and a design's page.
        // Availability below stays the garment-by-garment tool; same permission and feature, being
        // the same engine asked a broader question.
        key: 'showroom',
        icon: 'icon icon-shirt',
        label: 'Showroom',
        path: '/showroom',
        requires: 'Availability:View',
        feature: 'Availability',
      },
      {
        // The month, where Availability below is the "which garment for these dates" search. Same
        // permission and same feature: they are two questions asked of one engine, and a shop
        // allowed to ask one is allowed to ask the other.
        key: 'calendar',
        icon: 'icon icon-calendar-range',
        label: 'Calendar',
        path: '/calendar',
        requires: 'Availability:View',
        feature: 'Availability',
      },
      {
        key: 'availability',
        icon: 'icon icon-calendar-check',
        label: 'Availability',
        path: '/availability',
        requires: 'Availability:View',
        feature: 'Availability',
      },
      {
        key: 'returns',
        icon: 'icon icon-clipboard-check',
        label: 'Returns & inspection',
        path: '/returns',
        requires: 'ReturnInspection:View',
        feature: 'ReturnInspection',
      },
      {
        key: 'deposits',
        icon: 'icon icon-wallet',
        label: 'Deposits',
        path: '/deposits',
        requires: 'DepositSettlement:View',
        feature: 'ReturnInspection',
      },
      {
        key: 'invoices',
        icon: 'icon icon-file-text',
        label: 'Rental invoices',
        path: '/invoices',
        requires: 'SalesInvoice:View',
        feature: 'Billing',
      },
      // Gated on Approve rather than View, unlike everything else in this menu. A queue of things
      // waiting for *your* signature is noise to somebody who cannot sign one — counter staff hold
      // ReturnInspection read-write and no Approve, so they would see a list they could only read.
      {
        key: 'approvals',
        icon: 'icon icon-badge-check',
        label: 'Charge approvals',
        path: '/approvals',
        requires: 'ReturnInspection:Approve',
        feature: 'ReturnInspection',
      },
      // View, in contrast, because the counter is where a dispute is *recorded*. Closing one is
      // DepositSettlement:Approve and gated on the button — which the Accountant role holds
      // without holding ReturnInspection:Approve at all, so the two screens cannot share a gate.
      {
        key: 'disputes',
        icon: 'icon icon-flag',
        label: 'Disputes',
        path: '/disputes',
        requires: 'ReturnInspection:View',
        feature: 'ReturnInspection',
      },
    ],
  },
  {
    key: 'work',
    icon: 'icon icon-sparkles',
    label: 'Work',
    children: [
      {
        key: 'cleaning',
        icon: 'icon icon-washing-machine',
        label: 'Cleaning',
        path: '/cleaning',
        requires: 'CleaningJob:View',
        feature: 'Laundry',
      },
      // Feature-gated and not built yet. Both facts are shown: a plan that does not include
      // alterations renders it locked with an upgrade prompt rather than hiding it, because a
      // customer who never learns a module exists never buys it.
      {
        key: 'alterations',
        icon: 'icon icon-scissors',
        label: 'Alterations',
        path: '/alterations',
        requires: 'AlterationJob:View',
        feature: 'Alterations',
      },
      {
        key: 'fittings',
        icon: 'icon icon-ruler',
        label: 'Fittings',
        path: '/fittings',
        requires: 'FittingAppointment:View',
        feature: 'Fittings',
      },
    ],
  },
  {
    key: 'stock',
    icon: 'icon icon-boxes',
    label: 'Stock',
    children: [
      {
        key: 'units',
        icon: 'icon icon-tag',
        label: 'Item units',
        path: '/units',
        requires: 'ItemUnit:View',
        feature: 'UnitInventory',
      },
      {
        key: 'items',
        icon: 'icon icon-shirt',
        label: 'Items',
        path: '/items',
        requires: 'Item:View',
        feature: 'MasterData',
      },
      {
        key: 'receipts',
        icon: 'icon icon-package',
        label: 'Goods receipts',
        path: '/goods-receipts',
        requires: 'GoodsReceipt:View',
        feature: 'UnitInventory',
      },
      {
        key: 'transfers',
        icon: 'icon icon-truck',
        label: 'Transfers',
        path: '/transfers',
        requires: 'StockTransfer:View',
        feature: 'InterBranchTransfer',
      },
      // Items and units are separate entries for the same reason they are separate permission
      // modules: an item is the design the shop advertises, a unit is the garment on the rail, and
      // Section 6 exists to stop anyone treating the second as a quantity of the first.
      {
        key: 'categories',
        icon: 'icon icon-layers',
        label: 'Categories',
        path: '/categories',
        requires: 'ItemCategory:View',
        feature: 'MasterData',
      },
      // Sits next to categories rather than under items, because that is where it is used: an
      // attribute is the vocabulary a *category* draws on, and an item never names one directly.
      {
        key: 'attributes',
        icon: 'icon icon-sliders',
        label: 'Attributes',
        path: '/attributes',
        requires: 'ItemAttribute:View',
        feature: 'MasterData',
      },
      {
        key: 'ratecards',
        icon: 'icon icon-receipt-indian-rupee',
        label: 'Rate cards',
        path: '/rate-cards',
        requires: 'RateCard:View',
        feature: 'MasterData',
      },
      {
        key: 'packages',
        icon: 'icon icon-package-open',
        label: 'Packages',
        path: '/packages',
        requires: 'Package:View',
        feature: 'Packages',
      },
    ],
  },
  {
    key: 'shop',
    icon: 'icon icon-shopping-bag',
    label: 'Shop',
    children: [
      {
        // Retail sale (purchase step 5): sale invoices, under the Pos plan feature.
        key: 'pos',
        icon: 'icon icon-store',
        label: 'Sales',
        path: '/sell',
        requires: 'SalesInvoice:View',
        feature: 'Pos',
      },
      {
        key: 'purchases',
        icon: 'icon icon-shopping-cart',
        label: 'Purchases',
        path: '/purchases',
        requires: 'PurchaseOrder:View',
        feature: 'Purchase',
      },
      {
        key: 'bills',
        icon: 'icon icon-file-text',
        label: 'Supplier bills',
        path: '/supplier-bills',
        requires: 'PurchaseBill:View',
        feature: 'Purchase',
      },
    ],
  },
  {
    key: 'money',
    icon: 'icon icon-indian-rupee',
    label: 'Money',
    children: [
      {
        // ViewFinancials rather than View, and only the second place that action is enforced in
        // the product: the whole content of the screen is money, so a counter clerk who may see
        // the shop's work without seeing its cash position is a real role rather than a
        // theoretical one.
        key: 'cash',
        icon: 'icon icon-wallet',
        label: 'Cash & bank',
        path: '/cash',
        requires: 'CashAndBank:ViewFinancials',
        feature: 'CashAndExpense',
      },
      {
        key: 'expenses',
        icon: 'icon icon-receipt',
        label: 'Expenses',
        path: '/expenses',
        requires: 'Expense:View',
        feature: 'CashAndExpense',
      },
      {
        key: 'vendorpayments',
        icon: 'icon icon-wallet',
        label: 'Vendor payments',
        path: '/vendor-payments',
        requires: 'VendorPayment:View',
        feature: 'CashAndExpense',
      },
      {
        // ViewFinancials, not View: the lists are amounts and nothing else.
        key: 'outstanding',
        icon: 'icon icon-scale',
        label: 'Outstanding',
        path: '/outstanding',
        requires: 'Outstanding:ViewFinancials',
        feature: 'CashAndExpense',
      },
    ],
  },
  {
    key: 'people',
    icon: 'icon icon-contact-round',
    label: 'People',
    children: [
      {
        key: 'customers',
        icon: 'icon icon-contact',
        label: 'Customers',
        path: '/customers',
        requires: 'Customer:View',
        feature: 'MasterData',
      },
      // Beside the customers rather than under Wardrobe, because the two are the same kind of
      // thing: a party master. Which lists a vendor appears in — Section 10's dispatch, Section
      // 11's tailors, Section 16's suppliers — is a column on the row, not the screen it was
      // created from, so there is one entry here rather than three scattered through the menu.
      {
        key: 'vendors',
        icon: 'icon icon-truck',
        label: 'Vendors',
        path: '/vendors',
        requires: 'Vendor:View',
        feature: 'MasterData',
      },
      // The third party master, and the one that is inside the shop. It sits here rather than under
      // Setup beside Users because those two answer different questions: Users is who may sign in,
      // this is who works here — and most of these people never sign in at all.
      {
        key: 'employees',
        icon: 'icon icon-id-card',
        label: 'Staff',
        path: '/employees',
        requires: 'Employee:View',
        feature: 'MasterData',
      },
      // Last of the four, and the only one that is not a master: it is the identity the three above
      // share, read across all of them at once. Filed after them because that is the order somebody
      // learns them in — you know what a customer is before you need to know that the customer and
      // the karigar are one person.
      {
        key: 'parties',
        icon: 'icon icon-users-round',
        label: 'People',
        path: '/parties',
        requires: 'Party:View',
        feature: 'MasterData',
      },
    ],
  },
  {
    key: 'insight',
    icon: 'icon icon-chart-pie',
    label: 'Reports',
    children: [
      {
        key: 'reports',
        icon: 'icon icon-chart-column',
        label: 'Reports',
        path: '/reports',
        requires: 'Report:View',
      },
      {
        key: 'roi',
        icon: 'icon icon-trending-up',
        label: 'Unit ROI',
        path: '/unit-roi',
        requires: 'Report:View',
        feature: 'UnitRoiAnalytics',
      },
    ],
  },
  {
    key: 'setup',
    icon: 'icon icon-settings',
    label: 'Setup',
    children: [
      {
        key: 'company',
        icon: 'icon icon-building',
        label: 'Company profile',
        path: '/company',
        requires: 'Tenant:View',
      },
      {
        key: 'branch',
        icon: 'icon icon-building-2',
        label: 'Branches',
        path: '/branches',
        requires: 'Branch:View',
      },
      {
        key: 'user',
        icon: 'icon icon-users',
        label: 'Users',
        path: '/users',
        requires: 'User:View',
      },
      {
        key: 'role',
        icon: 'icon icon-shield-check',
        label: 'Roles',
        path: '/roles',
        requires: 'Role:View',
      },
      {
        key: 'fy',
        icon: 'icon icon-calendar-range',
        label: 'Financial years',
        path: '/financial-years',
        requires: 'FinancialYear:View',
      },
      {
        key: 'numbering',
        icon: 'icon icon-hash',
        label: 'Document numbering',
        path: '/numbering',
        requires: 'DocumentNumbering:View',
      },
      {
        key: 'settings',
        icon: 'icon icon-settings',
        label: 'Settings',
        path: '/settings',
        requires: 'Tenant:View',
      },
    ],
  },
];

/** A menu destination, with the group it sits under — enough to name it on a screen. */
export interface MenuDestination {
  key: string;
  label: string;
  /** The group heading above it, absent for a top-level item. */
  group?: string;
}

/**
 * Finds what the manifest says about a key, or null if it says nothing at all.
 *
 * The shell needs this to tell its two empty states apart. A key the manifest knows but no screen
 * answers is *coming soon* and can be named — "Bookings is part of Rental". A key the manifest has
 * never heard of is a 404, and inventing a label for it would be a guess presented as a fact.
 *
 * Unfiltered by permission or plan, on purpose: this answers "does this exist", not "may you see
 * it". `resolveMenu` is the one that decides visibility, and it is the sidebar's job, not this.
 */
export function findMenuDestination(
  key: string,
  nodes: MenuNode[] = menu,
  group?: string,
): MenuDestination | null {
  for (const node of nodes) {
    if (node.key === key) {
      return { key, label: node.label, ...(group ? { group } : {}) };
    }

    // A group's own label becomes the parent for everything below it. Nesting is two deep today
    // and the recursion does not assume that.
    const found = node.children ? findMenuDestination(key, node.children, node.label) : null;

    if (found) {
      return found;
    }
  }

  return null;
}

export interface ResolvedMenuNode extends Omit<MenuNode, 'children'> {
  /** The plan does not include it. Shown, but locked — see the note above. */
  locked: boolean;
  /**
   * Declared, but no screen behind it yet. Shown disabled rather than promising a dead route.
   *
   * Derived from {@link isBuilt}, never declared in the manifest above, so a screen becomes
   * reachable by existing rather than by someone remembering to delete a flag.
   */
  pending: boolean;
  children?: ResolvedMenuNode[];
}

/**
 * Applies the three axes.
 *
 * Permission decides whether an item appears at all — a module someone has no rights to is not
 * their business. Feature decides whether it appears *locked*. The read-only subscription state is
 * deliberately not applied here: a lapsed customer can still read, so modules stay visible and it
 * is the write actions inside them that are disabled.
 *
 * A group left with no visible children is dropped, or the sidebar renders empty accordions.
 */
export function resolveMenu(session: Session, nodes: MenuNode[] = menu): ResolvedMenuNode[] {
  return nodes.flatMap((node) => {
    if (!can(session, node.requires)) {
      return [];
    }

    const { children: declared, ...rest } = node;
    const children = declared ? resolveMenu(session, declared) : undefined;

    if (declared && (!children || children.length === 0)) {
      return [];
    }

    const resolved: ResolvedMenuNode = {
      ...rest,
      locked: !hasFeature(session, node.feature),
      // A group is never pending in its own right — it is as reachable as whatever it contains.
      pending: node.path !== undefined && !isBuilt(node.key),
      ...(children ? { children } : {}),
    };

    return [resolved];
  });
}
